import moment from 'moment';
import { getIn } from 'formik';

import {
  EXTRACTION_JOB_STATUS,
  INSURANCE_CARD_EXTRACTION_STORAGE_KEY_PREFIX,
  INSURANCE_DATE_FIELD_NAMES,
  INSURANCE_FORM_FIELD_NAMES,
  INSURANCE_INFORMATION_SECTION_FIELD_NAMES,
  INSURANCE_INITIAL_VALUES,
  MEMBER_REQUIRED_ADDRESS_FIELD_NAMES,
  MEMBER_TO_POLICYHOLDER_ADDRESS_FIELDS,
} from './constants';
import { INSURANCE_FORM_TEXT_MIN_LENGTH } from '../constants';
import { INSURANCE_FORM_SCHEMA } from '../validationSchema';
import { API_DATE_FORMAT, INSURANCE_TYPES } from '../../../../../../../constants';

import type { Moment } from 'moment';
import type { JsonValue } from '@openfeature/web-sdk';
import type {
  InsuranceCardExtractionResult,
  InsuranceExtractionCandidate,
  InsuranceDateFieldName,
  InsuranceExtractionEntry,
  InsuranceExtractionMap,
  InsuranceExtractionStatus,
  InsuranceFieldName,
  InsuranceFieldOverride,
  InsuranceFieldValue,
  InsurancePayload,
  InsuranceSavedFieldValue,
  InsuranceSavedFieldValues,
} from './types';
import type { InsuranceFormValues } from '../../../../types';
import type { InsuranceDocumentSubType } from './types';
import type { InsuranceType } from '../../../../../../../utils/types';
import type { IOnboardingQuestionnaireDocument } from '../../../../types';
import type { IOnboardingQuestionnaireInsurance } from '../types';

function getMomentValue(value: string | null | undefined): Moment | null {
  if (!value) {
    return null;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
}

function isStringValue(value: JsonValue): value is string {
  return Object.prototype.toString.call(value) === '[object String]';
}

export function findInsuranceByType(
  insurances: IOnboardingQuestionnaireInsurance[] | null | undefined,
  insuranceType: InsuranceType,
): IOnboardingQuestionnaireInsurance | undefined {
  return insurances?.find((insurance) => insurance.insurance_type === insuranceType);
}

export function getInsuranceCardDocument(
  documents: IOnboardingQuestionnaireDocument[] | null | undefined,
  documentSubType: InsuranceDocumentSubType,
): IOnboardingQuestionnaireDocument | undefined {
  return documents?.find((document) => document.document_sub_type === documentSubType);
}

export function getInsuranceInitialValues(
  insurance: IOnboardingQuestionnaireInsurance | null | undefined,
): InsuranceFormValues {
  if (insurance == null) {
    return INSURANCE_INITIAL_VALUES;
  }

  return {
    insurance_name: insurance.insurance_name ?? '',
    relationship_to_policyholder: insurance.relationship_to_policyholder ?? '',
    member_first_name: insurance.member_first_name ?? '',
    member_last_name: insurance.member_last_name ?? '',
    insurance_start_date: getMomentValue(insurance.insurance_start_date),
    insurance_end_date: getMomentValue(insurance.insurance_end_date),
    member_id: insurance.member_id ?? '',
    group_number: insurance.group_number ?? '',
    member_street_address_line_one: insurance.member_street_address_line_one ?? '',
    member_street_address_line_two: insurance.member_street_address_line_two ?? '',
    member_street_address_city: insurance.member_street_address_city ?? '',
    member_street_address_state: insurance.member_street_address_state ?? '',
    member_street_address_zip: insurance.member_street_address_zip ?? '',
    policyholder_first_name: insurance.policyholder_first_name ?? '',
    policyholder_last_name: insurance.policyholder_last_name ?? '',
    policyholder_date_of_birth: getMomentValue(insurance.policyholder_date_of_birth),
    policyholder_phone: insurance.policyholder_phone ?? '',
    policyholder_gender: insurance.policyholder_gender ?? '',
    policyholder_address_same_as_member: insurance.policyholder_address_same_as_member ?? false,
    policyholder_street_address_line_one: insurance.policyholder_street_address_line_one ?? '',
    policyholder_street_address_line_two: insurance.policyholder_street_address_line_two ?? '',
    policyholder_street_address_city: insurance.policyholder_street_address_city ?? '',
    policyholder_street_address_state: insurance.policyholder_street_address_state ?? '',
    policyholder_street_address_zip: insurance.policyholder_street_address_zip ?? '',
  };
}

export function getInsurancePayload(values: InsuranceFormValues): InsurancePayload {
  return {
    insurance_name: values.insurance_name || null,
    relationship_to_policyholder: values.relationship_to_policyholder || null,
    member_first_name: values.member_first_name || null,
    member_last_name: values.member_last_name || null,
    insurance_start_date: values.insurance_start_date?.format(API_DATE_FORMAT) ?? null,
    insurance_end_date: values.insurance_end_date?.format(API_DATE_FORMAT) ?? null,
    member_id: values.member_id || null,
    group_number: values.group_number || null,
    member_street_address_line_one: values.member_street_address_line_one || null,
    member_street_address_line_two: values.member_street_address_line_two || null,
    member_street_address_city: values.member_street_address_city || null,
    member_street_address_state: values.member_street_address_state || null,
    member_street_address_zip: values.member_street_address_zip || null,
    policyholder_first_name: values.policyholder_first_name || null,
    policyholder_last_name: values.policyholder_last_name || null,
    policyholder_date_of_birth: values.policyholder_date_of_birth?.format(API_DATE_FORMAT) ?? null,
    policyholder_phone: values.policyholder_phone || null,
    policyholder_gender: values.policyholder_gender || null,
    policyholder_address_same_as_member: values.policyholder_address_same_as_member,
    policyholder_street_address_line_one: values.policyholder_street_address_line_one || null,
    policyholder_street_address_line_two: values.policyholder_street_address_line_two || null,
    policyholder_street_address_city: values.policyholder_street_address_city || null,
    policyholder_street_address_state: values.policyholder_street_address_state || null,
    policyholder_street_address_zip: values.policyholder_street_address_zip || null,
  };
}

export function isInsuranceDateFieldName(name: InsuranceFieldName): name is InsuranceDateFieldName {
  return INSURANCE_DATE_FIELD_NAMES.some((fieldName) => fieldName === name);
}

export function getSavedFieldValue(
  name: InsuranceFieldName,
  values: InsuranceFormValues,
): InsuranceSavedFieldValue {
  if (isInsuranceDateFieldName(name)) {
    return values[name]?.format(API_DATE_FORMAT) ?? '';
  }

  const fieldValue = values[name];
  return fieldValue === true || fieldValue === false
    ? fieldValue
    : fieldValue == null
      ? ''
      : String(fieldValue);
}

export function getSavedFieldValues(values: InsuranceFormValues): InsuranceSavedFieldValues {
  return INSURANCE_FORM_FIELD_NAMES.reduce<InsuranceSavedFieldValues>(
    (savedValues, fieldName) => ({
      ...savedValues,
      [fieldName]: getSavedFieldValue(fieldName, values),
    }),
    {},
  );
}

export function getChangedSavedFieldValues(
  values: InsuranceFormValues,
  savedValues: InsuranceSavedFieldValues,
): InsuranceSavedFieldValues {
  return INSURANCE_FORM_FIELD_NAMES.reduce<InsuranceSavedFieldValues>((changed, fieldName) => {
    const savedValue = getSavedFieldValue(fieldName, values);
    if (savedValues[fieldName] === savedValue) {
      return changed;
    }
    return { ...changed, [fieldName]: savedValue };
  }, {});
}

export function getValuesWithFieldOverride(
  values: InsuranceFormValues,
  name: InsuranceFieldName,
  value: InsuranceFieldValue | undefined,
): InsuranceFormValues {
  if (value === undefined) {
    return values;
  }

  return { ...values, [name]: value };
}

export function applyInsuranceFieldOverrides(
  values: InsuranceFormValues,
  overrides: InsuranceFieldOverride[],
): InsuranceFormValues {
  return overrides.reduce<InsuranceFormValues>(
    (acc, { name, value }) => ({ ...acc, [name]: value }),
    values,
  );
}

export function isInsuranceDateFieldValid(
  name: InsuranceFieldName,
  values: InsuranceFormValues,
): boolean {
  if (!isInsuranceDateFieldName(name)) {
    return true;
  }

  const value = values[name];
  return value == null || value.isValid();
}

export async function isInsuranceFieldSchemaValid(
  name: InsuranceFieldName,
  values: InsuranceFormValues,
): Promise<boolean> {
  try {
    await INSURANCE_FORM_SCHEMA.validateAt(name, values);
    return true;
  } catch {
    return false;
  }
}

export function hasAllRequiredMemberAddressFields(values: InsuranceFormValues): boolean {
  return MEMBER_REQUIRED_ADDRESS_FIELD_NAMES.every((fieldName) =>
    Boolean(getIn(values, fieldName)),
  );
}

export function hasInsuranceNameAndMemberId(values: InsuranceFormValues): boolean {
  return (
    String(getIn(values, 'insurance_name') ?? '').trim().length >= INSURANCE_FORM_TEXT_MIN_LENGTH &&
    String(getIn(values, 'member_id') ?? '').trim().length >= INSURANCE_FORM_TEXT_MIN_LENGTH
  );
}

export function getPolicyholderSyncOverrides(
  values: InsuranceFormValues,
): InsuranceFieldOverride[] {
  return MEMBER_TO_POLICYHOLDER_ADDRESS_FIELDS.map(([memberFieldName, policyholderFieldName]) => ({
    name: policyholderFieldName,
    value: getIn(values, memberFieldName) ?? '',
  }));
}

export function isExtractionInProgress(status: InsuranceExtractionStatus): boolean {
  return status === EXTRACTION_JOB_STATUS.PENDING || status === EXTRACTION_JOB_STATUS.PROCESSING;
}

export function buildExtractionOverrides(
  result: InsuranceCardExtractionResult,
): InsuranceFieldOverride[] {
  const overrides: InsuranceFieldOverride[] = [];

  for (const name of INSURANCE_FORM_FIELD_NAMES) {
    const value = result[name];
    if (value === null || value === undefined) {
      continue;
    }

    if (isInsuranceDateFieldName(name)) {
      if (!isStringValue(value)) {
        continue;
      }
      const parsed = moment(value);
      if (parsed.isValid()) {
        overrides.push({ name, value: parsed });
      }
      continue;
    }

    overrides.push({ name, value });
  }

  return overrides;
}

export function hasFilledInsuranceInformation(
  insurance: IOnboardingQuestionnaireInsurance | null | undefined,
): boolean {
  const values = getInsuranceInitialValues(insurance);
  return INSURANCE_INFORMATION_SECTION_FIELD_NAMES.some((name) => {
    const value = values[name];
    return value != null && value !== '';
  });
}

export function buildExtractionInsurancePayload(
  insurance: IOnboardingQuestionnaireInsurance | null | undefined,
  result: InsuranceCardExtractionResult,
): InsurancePayload {
  const merged = applyInsuranceFieldOverrides(
    getInsuranceInitialValues(insurance),
    buildExtractionOverrides(result),
  );
  return getInsurancePayload(merged);
}

function createEmptyInsuranceExtractionMap(): InsuranceExtractionMap {
  return { [INSURANCE_TYPES.PRIMARY]: null, [INSURANCE_TYPES.SECONDARY]: null };
}

function isRecord(value: JsonValue): value is InsuranceExtractionCandidate {
  return value != null && Object.prototype.toString.call(value) === '[object Object]';
}

function isInsuranceExtractionStatus(value: JsonValue): value is InsuranceExtractionStatus {
  return Object.values(EXTRACTION_JOB_STATUS).some((status) => status === value);
}

function isInsuranceExtractionEntry(value: JsonValue): value is InsuranceExtractionEntry {
  if (!isRecord(value)) {
    return false;
  }

  return (
    'jobId' in value &&
    Number.isInteger(value.jobId) &&
    'status' in value &&
    isInsuranceExtractionStatus(value.status) &&
    'needsReview' in value &&
    (value.needsReview === true || value.needsReview === false) &&
    'reviewedAfterCompletion' in value &&
    (value.reviewedAfterCompletion === true || value.reviewedAfterCompletion === false)
  );
}

function getInsuranceExtractionStorageKey(onboardingQuestionnaireId: string): string {
  return `${INSURANCE_CARD_EXTRACTION_STORAGE_KEY_PREFIX}${onboardingQuestionnaireId}`;
}

export function readInsuranceExtractionStorage(
  onboardingQuestionnaireId: string,
): InsuranceExtractionMap {
  const map = createEmptyInsuranceExtractionMap();
  try {
    const raw = window.localStorage.getItem(
      getInsuranceExtractionStorageKey(onboardingQuestionnaireId),
    );
    if (!raw) {
      return map;
    }

    const parsed: JsonValue = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return map;
    }

    const primary = INSURANCE_TYPES.PRIMARY in parsed ? parsed[INSURANCE_TYPES.PRIMARY] : undefined;
    if (primary !== undefined && isInsuranceExtractionEntry(primary)) {
      map[INSURANCE_TYPES.PRIMARY] = primary;
    }
    const secondary =
      INSURANCE_TYPES.SECONDARY in parsed ? parsed[INSURANCE_TYPES.SECONDARY] : undefined;
    if (secondary !== undefined && isInsuranceExtractionEntry(secondary)) {
      map[INSURANCE_TYPES.SECONDARY] = secondary;
    }
    return map;
  } catch {
    // localStorage unavailable or corrupt JSON — fall back to an empty extraction map.
    return map;
  }
}

export function writeInsuranceExtractionEntry(
  onboardingQuestionnaireId: string,
  insuranceType: InsuranceType,
  entry: InsuranceExtractionEntry | null,
): void {
  const next: InsuranceExtractionMap = {
    ...readInsuranceExtractionStorage(onboardingQuestionnaireId),
    [insuranceType]: entry,
  };
  const key = getInsuranceExtractionStorageKey(onboardingQuestionnaireId);
  try {
    if (!next[INSURANCE_TYPES.PRIMARY] && !next[INSURANCE_TYPES.SECONDARY]) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(next));
    }
  } catch {
    // localStorage unavailable (private mode / quota) — extraction still works in-session.
  }
}
